import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/lib/constants";
import { MailCheck } from "lucide-react";

export const AuthShell = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-white px-4">
    <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 p-8 ys-fade-up">
      <Link to="/" className="flex items-center justify-center gap-2 mb-6">
        <img src={LOGO_URL} alt="Yard-skip" className="w-11 h-11 rounded-xl" />
        <span className="font-display font-extrabold text-xl">Yard-<span className="text-ys-sky">skip</span></span>
      </Link>
      {children}
    </div>
  </div>
);

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell>
      {sent ? (
        <div className="text-center" data-testid="forgot-sent">
          <MailCheck className="w-12 h-12 text-emerald-500 mx-auto" />
          <h1 className="font-display text-2xl font-bold mt-3">Check your email</h1>
          <p className="text-slate-500 text-sm mt-2">If <strong>{email}</strong> has an account, we've sent a reset link. It works once and expires in 1 hour.</p>
          <Link to="/login" className="inline-block mt-6 text-ys-sky font-semibold text-sm" data-testid="forgot-back-login">Back to sign in</Link>
        </div>
      ) : (
        <>
          <h1 className="font-display text-2xl font-bold text-center">Forgot your password?</h1>
          <p className="text-slate-500 text-sm text-center mt-1">Enter your email and we'll send you a link to reset it.</p>
          <form onSubmit={submit} className="space-y-4 mt-6">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email-input" className="mt-1.5 rounded-xl h-12" placeholder="you@example.com" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="forgot-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="forgot-submit-btn" className="w-full rounded-full h-12 bg-ys-sky hover:bg-sky-700 text-base">
              {loading ? "Sending..." : "Send reset link"}
            </Button>
          </form>
          <p className="text-sm text-center text-slate-500 mt-6">
            Remembered it? <Link to="/login" className="text-ys-sky font-semibold" data-testid="forgot-login-link">Sign in</Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}
